/* eslint-disable jsx-a11y/no-onchange, import/no-default-export */

import React, { useState } from 'react';

import Button from '@atlaskit/button/standard-button';
import { Field } from '@atlaskit/form';
import StarIcon from '@atlaskit/icon/glyph/star';
import StarFilledIcon from '@atlaskit/icon/glyph/star-filled';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import Select from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { setGlobalTheme } from '@atlaskit/tokens';
import {
  getCookie,
  setStrictlyNecessaryCookie,
  deleteCookie,
} from '@atlassian/browser-storage-controls';

import store from 'src/utils/store';

interface CoreIFrameWindow extends Window {
  setTheme: (theme: string) => void;
}

type LocationOption =
  | 'server'
  | 'canary'
  | 'local'
  | 'experiment'
  | 'pr'
  | 'pr-experiment';
type CookieValue =
  | 'server'
  | 'canary'
  | 'local'
  | 'experiment'
  | ['pr', Number]
  | ['pr-experiment', Number];

type PipelineEnvOption = 'ddev' | 'stg-west' | '';

export type ModeOptions = 'light' | 'dark';
export const globalThemeKey = 'bitbucket.global.theme';
export const getTheme = (): ModeOptions => {
  return store.get(globalThemeKey) || 'light';
};
const getCookieValue = (): CookieValue => {
  const cookie = getCookie('bbc-frontbucket-version');

  if (!cookie) {
    return 'server';
  }
  if (!cookie) {
    return 'server';
  }
  if (cookie === 'local') {
    return 'local';
  }
  if (cookie === 'canary') {
    return 'canary';
  }
  if (cookie === 'experiment') {
    return 'experiment';
  }
  const prIdMatch = /(\d+)(exp)?/.exec(cookie);
  if (prIdMatch) {
    const prId = parseInt(prIdMatch[1], 10);
    const isExperiment = prIdMatch[2];
    return [isExperiment ? 'pr-experiment' : 'pr', prId];
  }
  // Fall back to server
  return 'server';
};

const getPipelinesEnvCookieValue = (): PipelineEnvOption => {
  const cookie = getCookie('bbc-frontbucket-pipelines-env');

  if (cookie === 'ddev' || cookie === 'stg-west') {
    return cookie;
  }

  return '';
};

export default function VersionSelector(): JSX.Element {
  const initialValue: CookieValue = getCookieValue();
  let initialOption: LocationOption = 'server';
  const initialPipelinesEnvOption: PipelineEnvOption =
    getPipelinesEnvCookieValue();
  let initialPrId = '';

  // If we're looking at a frontbucket PR, use it as the default value for "from pull request"
  // If there's a different value in the cookie, that will override this value later.
  const matches = window.location.pathname.match(
    /^\/bitbucket\/frontbucket\/pull-requests\/(\d+)/
  );
  if (matches) {
    initialPrId = matches[1];
  }

  if (
    initialValue === 'local' ||
    initialValue === 'canary' ||
    initialValue === 'experiment'
  ) {
    initialOption = initialValue;
  } else if (Array.isArray(initialValue)) {
    initialOption = initialValue[0];
    initialPrId = `${initialValue[1]}`;
  }

  const [isOptionsVisible, setOptionsVisible] = useState(false);
  const [currentOption, setCurrentOption] =
    useState<LocationOption>(initialOption);
  const [currentPipelinesEnvOption, setCurrentPipelinesEnvOption] =
    useState<PipelineEnvOption>(initialPipelinesEnvOption);
  const [theme, setTheme] = useState<ModeOptions>(getTheme());
  const [prId, setPrId] = useState(initialPrId);
  const [expiry, setExpiry] = useState<number | undefined>(undefined);

  setGlobalTheme({ colorMode: theme });

  // In cases where we iframe embed core UI pages in repo & workspace settings,
  // we need to set the theme explicitly in the Core UI JS runtime.
  const settingsFrame = document.getElementById(
    'settings-frame'
  ) as HTMLIFrameElement | null;
  const settingsFrameWindow: null | undefined | CoreIFrameWindow =
    settingsFrame?.contentWindow as CoreIFrameWindow;
  if (
    settingsFrameWindow &&
    typeof settingsFrameWindow.setTheme === 'function'
  ) {
    settingsFrameWindow.setTheme(theme);
  }

  const toggleOptions = () => {
    setOptionsVisible(!isOptionsVisible);
  };
  const closeModal = () => setOptionsVisible(false);

  const setVersionCookie = () => {
    if (
      currentOption === 'local' ||
      currentOption === 'canary' ||
      currentOption === 'experiment'
    ) {
      setStrictlyNecessaryCookie('bbc-frontbucket-version', currentOption, {
        path: '/',
        expires: expiry,
      });
    } else if (currentOption === 'pr') {
      setStrictlyNecessaryCookie('bbc-frontbucket-version', prId, {
        path: '/',
        expires: expiry,
      });
    } else if (currentOption === 'pr-experiment') {
      setStrictlyNecessaryCookie('bbc-frontbucket-version', `${prId}exp`, {
        path: '/',
        expires: expiry,
      });
    } else {
      // delete the cookie
      deleteCookie('bbc-frontbucket-version');
    }
  };

  const setPipelinesEnvCookie = () => {
    if (
      currentPipelinesEnvOption === 'ddev' ||
      currentPipelinesEnvOption === 'stg-west'
    ) {
      setStrictlyNecessaryCookie(
        'bbc-frontbucket-pipelines-env',
        currentPipelinesEnvOption,
        {
          path: '/',
          expires: expiry,
        }
      );
    } else {
      deleteCookie('bbc-frontbucket-pipelines-env');
    }
  };

  const useAssets = () => {
    setVersionCookie();
    setPipelinesEnvCookie();

    window.location.reload();
  };

  const locationOptions: Array<{ label: string; value: LocationOption }> = [
    { label: 'Local', value: 'local' },
    { label: 'Canary', value: 'canary' },
    { label: 'Experiment', value: 'experiment' },
    { label: 'Pull request', value: 'pr' },
    { label: 'Pull request (experimental)', value: 'pr-experiment' },
    { label: 'Current server', value: 'server' },
  ];
  const sessionOptions = [
    { label: 'Session', value: undefined },
    { label: '1 hour', value: 3600 },
  ];
  const pipelineEnvOptions: Array<{
    label: string;
    value: PipelineEnvOption;
  }> = [
    { label: 'DDev', value: 'ddev' },
    { label: 'Staging', value: 'stg-west' },
    { label: 'Production', value: '' },
  ];
  const modeOptions: Array<{ label: string; value: ModeOptions }> = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
  ];
  return (
    <>
      <ModalTransition>
        {isOptionsVisible && (
          <Modal onClose={closeModal}>
            <ModalHeader>
              <ModalTitle>Frontbucket dev assets</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <Field name="location" label="Load assets from">
                {() => (
                  <Select
                    options={locationOptions}
                    defaultValue={locationOptions.find(
                      o => o.value === initialOption
                    )}
                    isSearchable={false}
                    onChange={e => {
                      if (e && 'value' in e) {
                        setCurrentOption(e.value);
                      }
                    }}
                  />
                )}
              </Field>

              <Field name="pullrequestId" label="Pull request ID">
                {() => (
                  <Textfield
                    isDisabled={
                      !(
                        currentOption === 'pr' ||
                        currentOption === 'pr-experiment'
                      )
                    }
                    placeholder="5271"
                    value={prId}
                    onChange={e => setPrId(e.currentTarget.value)}
                  />
                )}
              </Field>

              <Field name="pipelinesEnv" label="Pipelines Environment">
                {() => (
                  <Select
                    options={pipelineEnvOptions}
                    defaultValue={pipelineEnvOptions.find(
                      o => o.value === initialPipelinesEnvOption
                    )}
                    isSearchable={false}
                    onChange={e => {
                      if (e && 'value' in e) {
                        setCurrentPipelinesEnvOption(e.value);
                      }
                    }}
                  />
                )}
              </Field>

              <Field name="expiration" label="Expire after">
                {() => (
                  <Select
                    isDisabled={currentOption === 'server'}
                    isSearchable={false}
                    options={sessionOptions}
                    defaultValue={sessionOptions[0]}
                    onChange={e => {
                      if (e && 'value' in e) {
                        setExpiry(e.value);
                      }
                    }}
                  />
                )}
              </Field>

              <Field name="mode" label="Color mode">
                {() => (
                  <Select
                    options={modeOptions}
                    defaultValue={
                      modeOptions.find(m => m.value === theme) || modeOptions[0]
                    }
                    onChange={e => {
                      if (e && 'value' in e) {
                        setTheme(e.value);
                        store.set(globalThemeKey, e.value);
                      }
                    }}
                  />
                )}
              </Field>

              <p>
                In order to use local assets, you need to run parcel with HTTPS
                support.
              </p>
              <pre>
                <code>yarn start --https</code>
              </pre>
              <p>
                You’ll also need to manually accept the self-signed certificate
                from parcel.
              </p>
              <p>
                <a
                  href="https://localhost:3000/static/vendor.css"
                  target="_blank"
                >
                  View local assets
                </a>
              </p>
            </ModalBody>
            <ModalFooter>
              <Button appearance="primary" onClick={useAssets} autoFocus>
                Make it so
              </Button>
              <Button onClick={closeModal}>Cancel</Button>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>

      <div
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 100000,
        }}
      >
        <Button
          iconBefore={
            initialValue !== 'server' || getPipelinesEnvCookieValue() ? (
              <StarFilledIcon size="small" label="Toggle dev assets" />
            ) : (
              <StarIcon size="small" label="Toggle dev assets" />
            )
          }
          onClick={toggleOptions}
        />
      </div>
    </>
  );
}
